import { useShowContext } from 'framework/show';
import { useMessageService, ErrorMessage, SuccessMessage } from 'framework/ui/Message';
import { StaffingDataType } from 'domain/shared/staffing';
import { CHANGE_STAFFING_COORDINATOR } from 'domain/staffing/dashboard/constants';
import { useNewProjectService } from 'domain/shared/newProject';
import { useAuthorization } from 'framework/authorization';
import useAuthenticatedUserService from 'infrastructure/security/authentication/authenticatedUserService';
import { useAsyncValue } from 'framework/utils';
import useStaffingCoordinatorsService from './useStaffingCoordinatorsService';

export const useStaffingCoordinatorManagement = () => {
  const { data, setData } = useShowContext<StaffingDataType>();
  const { updateStaffingCoordinator } = useNewProjectService();
  const { addMessage } = useMessageService();
  const { employees } = useStaffingCoordinatorsService();
  const { isGranted } = useAuthorization();
  const { getUserAlias } = useAuthenticatedUserService();
  const currentUserAlias = useAsyncValue(() => getUserAlias());

  const hasChangePermission = isGranted(CHANGE_STAFFING_COORDINATOR);

  const projectData = data?.getData();
  const projectId = projectData?.id;

  const handleChangeStaffingCoordinator = async (selectedEmployeeAlias: string | null) => {
    try {
      await updateStaffingCoordinator(projectId!, selectedEmployeeAlias);
      setData!({
        ...projectData,
        staffingCoordinator: selectedEmployeeAlias ?? undefined,
      });
      const message = selectedEmployeeAlias
        ? 'Staffing coordinator changed successfully'
        : 'Staffing coordinator unassigned successfully';
      addMessage(new SuccessMessage(message));
    } catch (error) {
      addMessage(new ErrorMessage('Failed to change staffing coordinator'));
    }
  };

  const availableEmployees = hasChangePermission ? employees : employees.filter(emp => emp.alias === currentUserAlias);

  const coordinatorOptions = [
    ...(hasChangePermission
      ? [
          {
            key: 'unassign-coordinator',
            label: 'Unassign',
            disabled: !projectData?.staffingCoordinator,
            onClick: () => handleChangeStaffingCoordinator(null),
          },
        ]
      : []),
    ...availableEmployees.map(employee => ({
      key: `coordinator-${employee.alias}`,
      label: employee.name,
      disabled: employee.alias === projectData?.staffingCoordinator,
      onClick: () => handleChangeStaffingCoordinator(employee.alias),
    })),
  ];

  const assignCallbacks = {
    onAssign: async (_entityId: string, assigneeAlias: string | null) => {
      await handleChangeStaffingCoordinator(assigneeAlias);
    },
  };

  return {
    hasChangePermission,
    currentUserAlias: currentUserAlias ?? undefined,
    handleChangeStaffingCoordinator,
    coordinatorOptions,
    assignCallbacks,
    assigneeOptions: availableEmployees,
  };
};
